import {inject, Injectable} from "@angular/core";
import {GatewayApiClient} from "src/app/infrastructure/moovement-api/web-api/gateway.api-client";
import {GatewayStore} from "src/app/domain/devices/gateway/gateway.store";
import {tap} from "rxjs";
import {LoRaGateway, SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {
  InternalNotificationsStore,
  InternalNotificationType
} from "src/app/infrastructure/internal-notifications/internal-notifications.store";
import {FarmApiClient} from "src/app/infrastructure/moovement-api/web-api/farm.api-client";
import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {DataType} from "src/app/domain/domain-models";

@Injectable({
  providedIn: 'root'
})
export class GatewayService {

  private gatewayStore = inject(GatewayStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);

  constructor(
    private dataStorageService: DataStorageService,
    private farmApiClient: FarmApiClient,
    private gatewayApiClient: GatewayApiClient) {}

  reset() {
    this.gatewayStore.reset();
  }

  loadGateways(farmId: string): void {
    this.farmApiClient.getGateways(farmId).subscribe({
      next: async loRaGateways => {
        for (const gateway of loRaGateways) {
          await this.persist(gateway);
        }

        this.gatewayStore.setAllEntities(loRaGateways);
      },
      error: async () => {
        const cachedGateways = await this.dataStorageService.getAllActiveAssetsMatching<LoRaGateway>(DataType.loRaGateways, farmId);
        this.gatewayStore.setAllEntities(cachedGateways);
      }
    });
  }

  private async persist(gateway: LoRaGateway) {
    await this.dataStorageService.saveAsset(DataType.loRaGateways, gateway.farmId, gateway, SynchronizationStatus.upToDate);
  }

  create(gateway: LoRaGateway) {
    return this.gatewayApiClient.create(gateway).pipe(
      tap(gateway => this.gatewayStore.setEntity(gateway)),
      tap(gateway => this.persist(gateway)),
      tap(() => this.internalNotificationsStore.addNotification("Successfully created LoRa gateway.", InternalNotificationType.SUCCESS, false))
    );
  }

  get(id: string) {
    return this.gatewayApiClient.get(id);
  }

  merge(gateway: LoRaGateway) {
    return this.gatewayApiClient.merge(gateway).pipe(
      tap(gateway => this.gatewayStore.setEntity(gateway)),
      tap(gateway => this.persist(gateway)),
      tap(() => this.internalNotificationsStore.addNotification("Successfully updated LoRa gateway.", InternalNotificationType.SUCCESS, false))
    );
  }

  unassign(gateway: LoRaGateway) {
    return this.gatewayApiClient.unassign(gateway).pipe(
      tap(() => this.gatewayStore.removeEntity(gateway.id)),
      tap(() => this.dataStorageService.deleteAsset(gateway.id)),
      tap(() => this.internalNotificationsStore.addNotification("Successfully deleted LoRa gateway.", InternalNotificationType.SUCCESS, false))
    );
  }

  getStatus(eui: string) {
    return this.gatewayApiClient.getStatus(eui);
  }
}
